/*
 authService

 This class handles any authorization and authentication functionality

 */

import $ from 'jquery';
import _ from 'lodash';
import { uiHelper } from '../common';

const ssoKey = 'sso';

var AuthService = (function () {
	function authService() {

	}

	/**
	 * @param {Partial<AuthResponse>} auth 
	 */
	var setAuthenticationValues = function ({ userId, appUsername, authUsername, token, permissions, persistUserTokensAcrossSessions } = {}) {
		//If persistUserTokensAcrossSessions == true, the user token is stored in LocalStorage and wil persist even when the user
		//closes the browser window. The api processes server side will determine when to expire the token.
		//If persistUserTokensAcrossSessions == false, the token is stored in a session cookie (no expiration set), which
		//will be deleted when the browser is closed.
		//NOTE: I found that Chrome did not delete the cookie we're using to store the token upon 
		//closing the browser window. This was because I have a Chrome setting to enable background
		//Chrome process to run even if Chrome is closed. See link below:
		//http://stackoverflow.com/questions/10617954/chrome-doesnt-delete-session-cookies

		//If null or undefined, don't change the stored value
		var persistToken = localStorage.getItem("persistToken");
		if (persistUserTokensAcrossSessions !== undefined && persistUserTokensAcrossSessions !== null) {
			persistToken = persistUserTokensAcrossSessions ? "1" : "0";
			localStorage.setItem("persistToken", persistToken);
		}

		setAuthValue("authUsername", authUsername);

		//Store the username
		if (appUsername) {
			localStorage.setItem("loginUser", appUsername);
		}
		else {
			localStorage.removeItem("loginUser");
		}

		setAuthValue("userId", userId);

		//Store the userPermissions
		if (!_.isNullOrUndefined(permissions)) {
			localStorage.setItem("userPermissions", permissions);
		}
		else {
			localStorage.removeItem("userPermissions");
		}

		if (token) {
			if (persistToken === "1") {
				localStorage.setItem("userToken", token);
			}
			else {
				uiHelper.setCookie("userToken", token);
			}
		}
	};

	var getToken = function () {
		return uiHelper.getCookie("userToken") || localStorage.getItem("userToken");
	}

	/**
	 * Authenticate the user credentials with the api
	 * 
	 * @param {string} username 
	 * @param {string} password 
	 * @param {string} mealTicketUser 
	 * @returns {Promise<void>}
	 */
	authService.prototype.authenticateUser = function (username, password, mealTicketUser) {
		const url = new URL(`${API_URL}/account/authenticate`, location.href);

		var credentials = {};
		credentials.username = username;
		credentials.password = password;

		var jsonDataString = JSON.stringify(credentials);

		if (mealTicketUser) url.searchParams.set('mealTicketUser', mealTicketUser);

		return fetch(url.toString(), {
			method: 'POST',
			body: jsonDataString,
			processData: false,
			headers: { 'Content-Type': 'application/json; charset=utf-8' }
		})
			.then(resp => {
				if (resp.ok) return resp.json();
				else if ([401, 403].includes(resp.status)) throw 'Incorrect username or password.';
				else throw 'Unexpected error';
			})
			.then(resp => resp.url ? new Promise(_ => location.assign(resp.url)) : setAuthenticationValues(resp))
			.catch((reason) => {
				setAuthenticationValues();
				throw reason;
			});
	}

	/**
	 * Retrieves the URLs for interacting with the SSO provider.
	 */
	authService.prototype.getSsoUrls = function () {
		if (this.ssoUrls) return this.ssoUrls;

		return this.ssoUrls =
			fetch(`${API_URL}/account/sso-urls`).then(response => /** @type {SsoUrls?} */(response.json()));
	}

	/**
	 * Retrieves a user and loads it into the client.
	 * 
	 * @param {UserSecurityProperties | undefined} auth The user to load, or none to load from the client storage.
	 */
	authService.prototype.getUser = function (auth) {
		return fetch(`${API_URL}/account/current-user`, {
			headers: { Authorization: this.getAuthorizationHeaderValue(auth) }
		})
			.then(response =>
				response.ok ? /** @type {Promise<AuthResponse>} **/(response.json()) : Promise.reject(response)
			)
			.then(response => {
				setAuthenticationValues(response);
				return this.loadInsightUser().then(() => response);
			})
			.catch(response => {
				setAuthenticationValues();
				throw response;
			});
	}

	/**
	 * Clears the user's auth info from the client.
	 */
	authService.prototype.clearAuthentication = function () {
		setAuthenticationValues();
	}

	//Handle errors returned from api calls
	authService.prototype.handleApiResult = function (jqXhr) {

		var currentUrl = window.location.href;

		if (jqXhr.status === 401) {
			goToLoginPage();
		}
		else if (jqXhr.status === 403) {
			//Redirect to unauthorized page
			var unauthorizedPage = currentUrl.substring(0, currentUrl.lastIndexOf("/"));
			unauthorizedPage += "/unauthorized.html";
			window.location.href = unauthorizedPage;
		}
	}

	/**
	 * Loads the SSO user who just logged in to the client.
	 */
	authService.prototype.initializeSsoUser = function () {
		const sso = /** @type {SsoAuth} **/ (uiHelper.getUriEncodedCookie(ssoKey));

		return this
			.getUser({ token: sso.token, username: sso.userId })
			.then(_ => uiHelper.deleteCookie(ssoKey))
			.catch(response => this.handleApiResult(response))
	}

	/**
	 * Loads the current Insight user into the client.
	 */
	authService.prototype.loadInsightUser = function () {
		return fetch(`${API_URL}/account/current-insight-user`, {
			headers: { Authorization: this.getAuthorizationHeaderValue() }
		})
			.then(response => /** @type {Promise<InsightUser>} */(response.json()))
			.then(({ isAdminUser }) => localStorage.setItem("isAdminUser", isAdminUser))
	}

	/**
	 * Indicates whether the current user logged in via SSO.
	 */
	authService.prototype.loggedInSso = function () {
		return uiHelper.hasCookie(ssoKey);
	}

	//Get the URL that the user should be redirected to upon logging in.
	authService.prototype.getLoginRedirectUrl = function () {
		return localStorage.getItem("loginRedirect");
	}

	authService.prototype.isAdminUser = function () {
		var isAdmin = localStorage.getItem("isAdminUser");
		return isAdmin == "true";
	}

	authService.prototype.userPermissions = function () {
		var perms = localStorage.getItem("userPermissions");
		return perms || [];
	}

	authService.prototype.getLoginUser = function () {
		const user = localStorage.getItem("loginUser");

		if (user) return user; else goToLoginPage();
	}

	authService.prototype.getSupportInfo = function () {
		var deferred = $.Deferred();

		$.ajax({
			url: `${API_URL}/supportinfo`,
			type: 'GET',
			beforeSend: function (xhr) {
				xhr.setRequestHeader("Authorization", localAuthService.getAuthorizationHeaderValue());
			},
			success: function (results) {
				deferred.resolve(results);
			},
			error: function (jqXhr, status, error) {
				localAuthService.handleApiResult(jqXhr);
				deferred.reject(jqXhr);
			}
		});

		return deferred.promise();
	}

	authService.prototype.getDataProcessingCurrentStatus = function () {
		var deferred = $.Deferred();

		$.ajax({
			url: `${API_URL}/dataprocessing/currentstatus`,
			type: 'GET',
			beforeSend: function (xhr) {
				xhr.setRequestHeader("Authorization", localAuthService.getAuthorizationHeaderValue());
			},
			success: function (results) {
				deferred.resolve(results);
			},
			error: function (jqXhr, status, error) {
				localAuthService.handleApiResult(jqXhr);
				deferred.reject(jqXhr);
			}
		});

		return deferred.promise();
	}

	/**
	 * Get the stored auth token value to pass along in the
	 * ajax request header for api calls
	 *
	 * @param {UserSecurityProperties | undefined} auth 
	 */
	authService.prototype.getAuthorizationHeaderValue = function ({ token, username } = {
		token: getToken(),
		username: localStorage.getItem('authUsername')
	}) {
		return `Basic ${username}:${token}`;
	}

	authService.prototype.changePassword = function (newPassword) {
		var deferred = $.Deferred();

		$.ajax({
			url: `${API_URL}/ChangeLogin?newpass=` + newPassword,
			type: 'GET',
			beforeSend: function (xhr) {
				xhr.setRequestHeader("Authorization", localAuthService.getAuthorizationHeaderValue());
			},
			success: function (results, status, jqXhr) {

				deferred.resolve(results);
			},
			error: function (jqXhr, status, error) {
				localAuthService.handleApiResult(jqXhr);
				deferred.reject(jqXhr);
			}
		});

		return deferred.promise();

	}

	authService.prototype.sendPasswordResetEmail = function (emailAddress) {
		var deferred = $.Deferred();

		var request = {};
		request.emailAddress = emailAddress;

		var jsonDataString = JSON.stringify(request);

		$.ajax({
			url: `${API_URL}/account/sendpasswordresetemail`,
			type: 'POST',
			data: jsonDataString,
			processData: false,
			contentType: 'application/json; charset=utf-8',
			dataType: 'json',
			success: function (response) {
				deferred.resolve(response);
			},
			error: function (jqXhr, status, error) {
				deferred.reject(jqXhr);
			}
		});

		return deferred.promise();
	}

	authService.prototype.checkPasswordResetTokenStatus = function (tokenValue) {
		var deferred = $.Deferred();

		var request = {};
		request.tokenValue = tokenValue;

		var jsonDataString = JSON.stringify(request);

		$.ajax({
			url: `${API_URL}/account/checkpasswordresettokenstatus`,
			type: 'POST',
			data: jsonDataString,
			processData: false,
			contentType: 'application/json; charset=utf-8',
			dataType: 'json',
			success: function (response) {
				deferred.resolve(response);
			},
			error: function (jqXhr, status, error) {
				deferred.reject(jqXhr);
			}
		});

		return deferred.promise();
	}

	authService.prototype.resetPassword = function (newPassword, tokenValue) {
		var deferred = $.Deferred();

		var request = {};
		request.newPassword = newPassword;
		request.tokenValue = tokenValue;

		var jsonDataString = JSON.stringify(request);

		$.ajax({
			url: `${API_URL}/account/resetpassword`,
			type: 'POST',
			data: jsonDataString,
			processData: false,
			contentType: 'application/json; charset=utf-8',
			dataType: 'json',
			success: function (response) {
				deferred.resolve(response);
			},
			error: function (jqXhr, status, error) {
				deferred.reject(jqXhr);
			}
		});

		return deferred.promise();
	}

	authService.prototype.sendUsernameReminderEmail = function (emailAddress) {

		var deferred = $.Deferred();

		var request = {};
		request.emailAddress = emailAddress;

		var jsonDataString = JSON.stringify(request);

		$.ajax({
			url: `${API_URL}/account/sendusernamereminderemail`,
			type: 'POST',
			data: jsonDataString,
			processData: false,
			contentType: 'application/json; charset=utf-8',
			dataType: 'json',
			success: function (response) {
				deferred.resolve(response);
			},
			error: function (jqXhr, status, error) {
				deferred.reject(jqXhr);
			}
		});

		return deferred.promise();
	}

	return authService;
})();

const localAuthService = new AuthService();

export const authService = localAuthService;

function goToLoginPage() {
	//Store the current url so that the login page can redirect later.
	localStorage.setItem('loginRedirect', location.href);

	//Redirect to login page
	var loginUrl = location.href.substring(0, location.href.lastIndexOf('/'));
	loginUrl += '/login.html';
	location.assign(loginUrl);
}

/**
 * @param {string} key 
 * @param {unknown} value 
 */
function setAuthValue(key, value) {
	if (value) localStorage.setItem(key, String(value));
	else localStorage.removeItem(key);
}

/**
 * @typedef {Object} AuthResponse
 * @property {string} email
 * @property {string[]} permissions
 * @property {boolean} persistUserTokensAcrossSessions
 * @property {string} token
 * @property {number} userId
 * @property {string} username
 */

/**
 * @typedef {Object} InsightUser
 * @property {boolean} isAdminUser
 */

/**
 * @typedef {Object} SsoAuth
 * @property {string} token
 * @property {string} userId
 */

/**
 * @typedef {Object} SsoRedirectResponse
 * @property {string} url
 */

/**
 * @typedef {Object} SsoUrls
 * @property {string | undefined} logout
 * @property {string | undefined} passwordReset
 */

/**
 * @typedef {Object} UserSecurityProperties
 * @property {string} token
 * @property {string} username
 */
